import React from "react";
import { Routes, Route } from "react-router-dom";

import Error404 from "@/pages/errors/Error404";
import Dashboard from "../pages/admin/Dashboard";

import Events from "../pages/admin/events/Events";
import EventEdit from "../pages/admin/events/EventEdit";
import AdminLayout from "../components/admin/AdminLayout";
import EventCreate from "../pages/admin/events/EventCreate";

import Projects from "@/pages/admin/projects/Projects";
import ProjectEdit from "@/pages/admin/projects/ProjectEdit";
import ProjectCreate from "@/pages/admin/projects/ProjectCreate";

import About from "@/pages/admin/settings/About";
import CoreValues from "@/pages/admin/settings/CoreValues";
import Announcements from "@/pages/admin/announcements/Announcements";
import AnnouncementCreate from "@/pages/admin/announcements/AnnouncementCreate";
import AnnouncementEdit from "@/pages/admin/announcements/AnnouncementEdit";
import Blogs from "@/pages/admin/blogs/Blogs";
import BlogCreate from "@/pages/admin/blogs/BlogCreate";
import BlogEdit from "@/pages/admin/blogs/BlogEdit";
import Users from "@/pages/admin/users/Users";
import Businesses from "@/pages/admin/businesses/Businesses";
import BusinessCreate from "@/pages/admin/businesses/BusinessCreate";
import BusinessEdit from "@/pages/admin/businesses/BusinessEdit";
import Attractions from "@/pages/admin/attractions/Attractions";
import AttractionCreate from "@/pages/admin/attractions/AttractionCreate";
import AttractionEdit from "@/pages/admin/attractions/AttractionEdit";
import Scholarships from "@/pages/admin/scholarships/Scholarships";
import ScholarshipCreate from "@/pages/admin/scholarships/ScholarshipCreate";
import ScholarshipEdit from "@/pages/admin/scholarships/ScholarshipEdit";
import Profile from "@/pages/admin/profile/Profile";
import ChangePassword from "@/pages/admin/profile/ChangePassword";

import PermissionBasedRoute from "../components/common/PermissionBasedRoute";

const AdminRoutes: React.FC = () => {
  return (
    <AdminLayout>
      <Routes>
        <Route path="dashboard" element={<Dashboard />} />

        {/* Users */}
        <Route
          path="users"
          element={
            <PermissionBasedRoute permission="view users">
              <Users />
            </PermissionBasedRoute>
          }
        />

        {/* Projects */}
        <Route
          path="projects"
          element={
            <PermissionBasedRoute
              permissions={[
                "view projects",
                "create projects",
                "edit projects",
                "delete projects",
              ]}
            >
              <Projects />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="projects/create"
          element={
            <PermissionBasedRoute permission="create projects">
              <ProjectCreate />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="projects/:id/edit"
          element={
            <PermissionBasedRoute permission="edit projects">
              <ProjectEdit />
            </PermissionBasedRoute>
          }
        />

        {/* Events */}
        <Route
          path="events"
          element={
            <PermissionBasedRoute
              permissions={[
                "view events",
                "create events",
                "edit events",
                "delete events",
              ]}
            >
              <Events />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="events/create"
          element={
            <PermissionBasedRoute permission="create events">
              <EventCreate />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="events/:id/edit"
          element={
            <PermissionBasedRoute permission="edit events">
              <EventEdit />
            </PermissionBasedRoute>
          }
        />

        {/* Announcements */}
        <Route
          path="announcements"
          element={
            <PermissionBasedRoute
              permissions={[
                "view announcements",
                "create announcements",
                "edit announcements",
                "delete announcements",
              ]}
            >
              <Announcements />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="announcements/create"
          element={
            <PermissionBasedRoute permission="create announcements">
              <AnnouncementCreate />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="announcements/:id/edit"
          element={
            <PermissionBasedRoute permission="edit announcements">
              <AnnouncementEdit />
            </PermissionBasedRoute>
          }
        />

        {/* Businesses */}
        <Route
          path="businesses"
          element={
            <PermissionBasedRoute
              permissions={[
                "view businesses",
                "create businesses",
                "edit businesses",
                "delete businesses",
              ]}
            >
              <Businesses />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="businesses/create"
          element={
            <PermissionBasedRoute permission="create businesses">
              <BusinessCreate />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="businesses/:id/edit"
          element={
            <PermissionBasedRoute permission="edit businesses">
              <BusinessEdit />
            </PermissionBasedRoute>
          }
        />

        {/* Attractions */}
        <Route
          path="attractions"
          element={
            <PermissionBasedRoute
              permissions={[
                "view attractions",
                "create attractions",
                "edit attractions",
                "delete attractions",
              ]}
            >
              <Attractions />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="attractions/create"
          element={
            <PermissionBasedRoute permission="create attractions">
              <AttractionCreate />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="attractions/:id/edit"
          element={
            <PermissionBasedRoute permission="edit attractions">
              <AttractionEdit />
            </PermissionBasedRoute>
          }
        />

        {/* Scholarships */}
        <Route
          path="scholarships"
          element={
            <PermissionBasedRoute
              permissions={[
                "view scholarships",
                "create scholarships",
                "edit scholarships",
                "delete scholarships",
              ]}
            >
              <Scholarships />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="scholarships/create"
          element={
            <PermissionBasedRoute permission="create scholarships">
              <ScholarshipCreate />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="scholarships/:id/edit"
          element={
            <PermissionBasedRoute permission="edit scholarships">
              <ScholarshipEdit />
            </PermissionBasedRoute>
          }
        />

        {/* Blogs */}
        <Route
          path="blogs"
          element={
            <PermissionBasedRoute
              permissions={[
                "view blogs",
                "create blogs",
                "edit blogs",
                "delete blogs",
              ]}
            >
              <Blogs />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="blogs/create"
          element={
            <PermissionBasedRoute permission="create blogs">
              <BlogCreate />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="blogs/:id/edit"
          element={
            <PermissionBasedRoute permission="edit blogs">
              <BlogEdit />
            </PermissionBasedRoute>
          }
        />

        {/* Settings */}
        <Route
          path="settings/about"
          element={
            <PermissionBasedRoute permission="manage settings">
              <About />
            </PermissionBasedRoute>
          }
        />
        <Route
          path="settings/core-values"
          element={
            <PermissionBasedRoute permission="manage settings">
              <CoreValues />
            </PermissionBasedRoute>
          }
        />

        {/* Profile */}
        <Route path="profile" element={<Profile />} />
        <Route path="change-password" element={<ChangePassword />} />

        <Route path="*" element={<Error404 />} />
      </Routes>
    </AdminLayout>
  );
};

export default AdminRoutes;
